// V1.9 — per-match medal grading. Gives losers a measurable thing to
// take away from a defeat ("Bronze in objective play") and gives winners
// a depth signal ("Gold — flawless"). The grade is computed from final
// MatchState + extra stats already tracked by EndOfMatch (kills, best
// streak), so no new simulation fields are needed.
//
// Tiers, evaluated top-down — first match wins:
//   GOLD     — win + viewer Core HP ratio ≥ 0.6 + ≥ 2 relays held at end
//              + ≥ 6 squads built. "Decisive victory."
//   SILVER   — win (any other shape). "Victory."
//   BRONZE   — loss with objective play: captured/held ≥ 1 relay at any
//              point during the match AND built ≥ 3 squads. "Hard-fought."
//   none     — neither win nor objective-play loss. Card is hidden.
//
// The "captured ≥ 1 relay at any point" check uses the relayHistory the
// sim records — a player who lost a relay before the match ended still
// gets credit for the achievement.

import type { MatchState, PlayerId } from '@signalclash/shared';
import { PLAYER_ONE, PLAYER_ZERO } from '@signalclash/shared';

export type MedalTier = 'gold' | 'silver' | 'bronze';

export interface Medal {
  tier: MedalTier;
  label: string;
  detail: string;
}

export interface MedalInputs {
  finalState: MatchState | null;
  viewerId: PlayerId;
  viewerKills: number;
  won: boolean;
}

const GOLD_CORE_HP_RATIO = 0.6;
const GOLD_RELAYS_HELD = 2;
const GOLD_SQUADS_BUILT = 6;
const BRONZE_SQUADS_BUILT = 3;

export function computeMatchMedal(inputs: MedalInputs): Medal | null {
  const { finalState, viewerId, viewerKills, won } = inputs;
  if (finalState === null) {
    // No final state to inspect — fall back to a flat win/loss medal so
    // the UI still gets something when the EOM is shown via the
    // observeSnapshot fallback path.
    return won
      ? { tier: 'silver', label: 'VICTORY', detail: 'Match complete.' }
      : null;
  }

  const relaysHeldEnd = countOwnedRelays(finalState, viewerId);
  const squadsBuilt = finalState.stats.squadsBuilt[viewerId] ?? 0;
  const coreHpRatio = computeViewerCoreHpRatio(finalState, viewerId);
  const heldAnyRelay = relaysHeldEnd > 0 || everHeldRelay(finalState, viewerId);

  if (won) {
    if (
      coreHpRatio >= GOLD_CORE_HP_RATIO &&
      relaysHeldEnd >= GOLD_RELAYS_HELD &&
      squadsBuilt >= GOLD_SQUADS_BUILT
    ) {
      return {
        tier: 'gold',
        label: 'GOLD — DECISIVE',
        detail: `Core at ${Math.round(coreHpRatio * 100)}%, ${squadsBuilt} squads, ${viewerKills} eliminations.`,
      };
    }
    return {
      tier: 'silver',
      label: 'SILVER — VICTORY',
      detail: `${squadsBuilt} squads built, ${viewerKills} eliminations.`,
    };
  }

  if (heldAnyRelay && squadsBuilt >= BRONZE_SQUADS_BUILT) {
    return {
      tier: 'bronze',
      label: 'BRONZE — HARD-FOUGHT',
      detail: `${squadsBuilt} squads, ${viewerKills} eliminations. Re-engage.`,
    };
  }

  return null;
}

function countOwnedRelays(state: MatchState, ownerId: PlayerId): number {
  let n = 0;
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === ownerId) n += 1;
  }
  return n;
}

function everHeldRelay(state: MatchState, viewerId: PlayerId): boolean {
  // RelaySample.nodesByPlayer indexes [PLAYER_ZERO, PLAYER_ONE].
  const idx = viewerId === PLAYER_ZERO ? 0 : 1;
  for (const s of state.stats.relayHistory) {
    if ((s.nodesByPlayer[idx] ?? 0) > 0) return true;
  }
  return false;
}

function computeViewerCoreHpRatio(state: MatchState, viewerId: PlayerId): number {
  // PLAYER_ZERO / PLAYER_ONE — keep variable name explicit so the
  // "viewerId === PLAYER_ZERO" branch reads naturally below.
  void PLAYER_ZERO;
  void PLAYER_ONE;
  for (const core of Object.values(state.cores)) {
    if (core.ownerId !== viewerId) continue;
    if (core.maxHp <= 0) return 0;
    return Math.max(0, Math.min(1, core.hp / core.maxHp));
  }
  return 0;
}
